'use client';
import type { ProductDocument, ProductLight } from '@models/product';
import { cn, formatCurrency } from '@util/index';

interface ProductPriceRangeProps {
  product: ProductDocument | ProductLight;
  className?: string;
  showSalePrice?: boolean;
  textColor?: 'white' | 'default';
  textSize?: 'default' | 'large';
  vertical?: boolean;
  enableCouponDiscount?: boolean;
  hideSaleBadge?: boolean;
}

const ProductPriceRange = ({
  product,
  className = '',
  showSalePrice = true,
  textColor = 'default',
  textSize = 'default',
  vertical = false,
  enableCouponDiscount = false,
  hideSaleBadge = false,
}: ProductPriceRangeProps) => {
  const fullPrice = product.price;

  const getSalePercent = () => {
    if (!product.on_sale) return 0;
    return Math.round(
      ((Number(product.og_price) - product.price) / Number(product.og_price)) *
        100
    );
  };

  const activeCoupon =
    enableCouponDiscount &&
    'coupons' in product &&
    Array.isArray(product.coupons)
      ? product.coupons.find((coupon) => coupon.active)
      : null;

  const salePercent = getSalePercent();

  const getCouponPercent = () => {
    if (!activeCoupon) return 0;
    if (activeCoupon.percent_off) return activeCoupon.percent_off;
    if (activeCoupon.amount_off && product.og_price) {
      return Math.round(
        (Number(activeCoupon.amount_off) / Number(product.og_price)) * 100
      );
    }
    return 0;
  };

  const couponPercent = getCouponPercent();
  const biggestDiscount = Math.max(salePercent, couponPercent);
  const isSaleBigger = salePercent >= couponPercent;

  const variationPriceRange = () => {
    if ('variation_pricing' in product && product.variation_pricing) {
      return product.variation_pricing;
    }

    if ('variations' in product && Array.isArray(product.variations)) {
      const isGearCombo =
        product.category2 === 'Gear Combos' &&
        product.variations.some((pv) => pv.is_number) &&
        product.variations.some((pv) => !pv.is_number);

      if (isGearCombo) {
        const numberSizes = product.variations.filter((pv) => pv.is_number);
        const letterSizes = product.variations.filter((pv) => !pv.is_number);

        const numberPrices = numberSizes.map((ns) => ns.price);
        const letterPrices = letterSizes.map((ls) => ls.price);

        const minNumberSizePrice = Math.min(...numberPrices);
        const maxNumberSizePrice = Math.max(...numberPrices);

        const minLetterSizePrice = Math.min(...letterPrices);
        const maxLetterSizePrice = Math.max(...letterPrices);

        const min = minNumberSizePrice + minLetterSizePrice;
        const max = maxNumberSizePrice + maxLetterSizePrice;
        if (min === max) return;
        return {
          minPrice: minNumberSizePrice + minLetterSizePrice,
          maxPrice: maxNumberSizePrice + maxLetterSizePrice,
        };
      } else {
        const filteredPrices = product.variations
          ?.filter((v) => v.price && v.qty > 0)
          .map((v) => v.price);

        if (!filteredPrices?.length) return;

        const minPrice = Math.min(...filteredPrices);
        const maxPrice = Math.max(...filteredPrices);

        if (minPrice === maxPrice) return;

        return { minPrice, maxPrice };
      }
    }

    return;
  };

  const { minPrice, maxPrice } = variationPriceRange() ?? {};
  if (minPrice && maxPrice) {
    return (
      <div className={cn('mt-1 flex items-center gap-2')}>
        <span
          className={cn(
            'font-semibold',
            textColor === 'white' ? 'text-white' : 'text-black',
            textSize === 'large'
              ? 'text-[2.1rem] sm:text-[2.3rem]'
              : 'text-[1.8rem] sm:text-[2rem]',
            className
          )}
        >
          {formatCurrency(minPrice)}
        </span>

        <div
          className={cn(
            'h-px w-6',
            textColor === 'white' ? 'bg-white' : 'bg-zinc-600'
          )}
        />

        <span
          className={cn(
            'font-semibold',
            textColor === 'white' ? 'text-white' : 'text-black',
            textSize === 'large'
              ? 'text-[2.1rem] sm:text-[2.3rem]'
              : 'text-[1.8rem] sm:text-[2rem]',
            className
          )}
        >
          {formatCurrency(maxPrice)}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mt-1 flex',
        vertical ? 'flex-col' : 'items-center gap-2',
        className
      )}
    >
      {product.on_sale && showSalePrice && vertical && (
        <div className="flex items-center gap-4">
          <span
            className={cn(
              'font-semibold',
              textColor === 'white' ? 'text-white' : 'text-black',
              textSize === 'large'
                ? 'text-[2.1rem] sm:text-[2.3rem]'
                : 'text-[1.8rem] sm:text-[2rem]',
              className
            )}
          >
            {formatCurrency(product.price)}
          </span>

          <span
            className={cn(
              'font-medium line-through',
              textColor === 'white' ? 'text-white' : 'text-zinc-500', // AA compliant text color
              textSize === 'large'
                ? 'text-[1.8rem] sm:text-[2rem]'
                : 'text-[1.6rem] sm:text-[1.8rem]',
              className
            )}
          >
            {formatCurrency(product.og_price)}
          </span>
        </div>
      )}

      {!product.on_sale && vertical && (
        <div className="flex items-center gap-4">
          <span
            className={cn(
              'font-semibold',
              textColor === 'white' ? 'text-white' : 'text-black',
              textSize === 'large'
                ? 'text-[2.1rem] sm:text-[2.3rem]'
                : 'text-[1.8rem] sm:text-[2rem]',
              className
            )}
          >
            {formatCurrency(fullPrice)}
          </span>
          <span className="invisible text-[1.6rem] font-medium sm:text-[1.8rem]">
            {formatCurrency(fullPrice)}
          </span>
        </div>
      )}

      {!product.on_sale && !vertical && (
        <span
          className={cn(
            'font-semibold',
            textColor === 'white' ? 'text-white' : 'text-black',
            textSize === 'large'
              ? 'text-[2.1rem] sm:text-[2.3rem]'
              : 'text-[1.8rem] sm:text-[2rem]',
            className
          )}
        >
          {formatCurrency(fullPrice)}
        </span>
      )}

      {biggestDiscount > 0 && (
        <>
          {isSaleBigger && product.on_sale && !vertical && (
            <span
              className={cn(
                'font-semibold',
                textColor === 'white' ? 'text-white' : 'text-black',
                textSize === 'large'
                  ? 'text-[2.1rem] sm:text-[2.3rem]'
                  : 'text-[1.8rem] sm:text-[2rem]',
                className
              )}
            >
              {formatCurrency(fullPrice)}
            </span>
          )}

          {(isSaleBigger ? product.on_sale : activeCoupon) &&
            !product.is_auction &&
            showSalePrice &&
            !vertical && (
              <span
                className={cn(
                  'font-normal line-through',
                  textColor === 'white' ? 'text-white' : 'text-zinc-700',
                  textSize === 'large'
                    ? 'text-[2.1rem] sm:text-[2.3rem]'
                    : 'text-[1.8rem] sm:text-[2rem]',
                  className
                )}
              >
                {formatCurrency(product.og_price)}
              </span>
            )}

          {isSaleBigger &&
            product.on_sale &&
            !product.is_auction &&
            !hideSaleBadge && (
              <span
                className={cn(
                  'font-semibold uppercase',
                  textColor === 'white' ? 'text-white' : 'text-brand-secondary',
                  textSize === 'large'
                    ? 'text-[2.1rem] sm:text-[2.3rem]'
                    : 'text-[1.8rem] sm:text-[2rem]',
                  className
                )}
              >
                {salePercent}% OFF
              </span>
            )}

          {!isSaleBigger &&
            activeCoupon &&
            !product.is_auction &&
            !hideSaleBadge && (
              <span
                className={cn(
                  'font-semibold',
                  textColor === 'white' ? 'text-white' : 'text-brand-secondary',
                  textSize === 'large'
                    ? 'text-[2.1rem] sm:text-[2.3rem]'
                    : 'text-[1.8rem] sm:text-[2rem]',
                  className
                )}
              >
                {activeCoupon.percent_off
                  ? `${activeCoupon.percent_off}% OFF`
                  : `${formatCurrency(activeCoupon.amount_off ?? 0)} OFF`}{' '}
                with coupon
              </span>
            )}
        </>
      )}
    </div>
  );
};

export default ProductPriceRange;
